import type { ReactNode } from 'react';
import { Box, Text, FeatureErrorBoundary } from '@citibox-panel/ui';
import {
  ROOT_SUBTAB,
  deliveryIdFromTabKey,
  isDeliveryTab,
  isLocationTab,
  type LocationTab,
  type SearchTab,
} from '../model/shellState';

export interface TabContentProps {
  search: SearchTab;
  // H3–H8 inject the real entity views; until then the shell shows placeholders.
  renderUser?: (search: SearchTab) => ReactNode;
  renderDelivery?: (search: SearchTab, deliveryId: string) => ReactNode;
  // A `LocationTab`, not a `SearchTab`: this is the single point every location view is reached
  // through, so narrowing here hands `label` and `filters` on already non-optional instead of
  // leaving each view to re-derive a guarantee the state model has already made.
  renderLocation?: (search: LocationTab) => ReactNode;
}

function Placeholder({ text }: { text: string }): ReactNode {
  return (
    <Box sx={{ p: 3 }}>
      {/* TODO: i18n */}
      <Text variant="body2" color="secondary">
        {text}
      </Text>
    </Box>
  );
}

export function TabContent({
  search,
  renderUser,
  renderDelivery,
  renderLocation,
}: TabContentProps) {
  let content: ReactNode;
  // The sub-tab decides first and the kind only inside the root case: a location tab reaches the
  // «Entrega» detail through the same branch as a «Usuario» one, and it resolves to the same entity
  // either way — neither the render tree nor the data depends on the kind. The kind survives on the
  // tab for the host to read: it opens the detail's «Acciones» card on the audience that matches
  // where the agent came from.
  if (search.activeSubtab === ROOT_SUBTAB) {
    if (search.kind === 'location') {
      // Asked on `kind` and narrowed separately, so a location tab that somehow lost its
      // `filters` degrades to the placeholder instead of painting an «Usuario» ficha.
      content =
        isLocationTab(search) && renderLocation ? (
          renderLocation(search)
        ) : (
          <Placeholder text="Entregas de la ubicación (próximamente)" />
        );
    } else if (search.kind === 'delivery') {
      // A tab that IS one delivery: the root is where the detail goes, and the id comes off the
      // tab's own key instead of a sub-tab. Asked on `kind` and narrowed separately for the same
      // reason as above — a key carrying no id degrades to the placeholder rather than reading an
      // empty string into a request path, and never falls through to the «Usuario» ficha for a
      // delivery that has no «Usuario».
      content =
        isDeliveryTab(search) && renderDelivery ? (
          renderDelivery(search, deliveryIdFromTabKey(search.key))
        ) : (
          <Placeholder text="Detalle de entrega (próximamente)" />
        );
    } else {
      content = renderUser ? (
        renderUser(search)
      ) : (
        <Placeholder text="Ficha de usuario (próximamente)" />
      );
    }
  } else {
    content = renderDelivery ? (
      renderDelivery(search, search.activeSubtab)
    ) : (
      <Placeholder text="Detalle de entrega (próximamente)" />
    );
  }

  // One boundary per active sub-tab (keyed by it): a broken entity view degrades to a
  // retriable alert instead of taking the shell down, and — because the boundary latches
  // its error until reset — keying by the sub-tab remounts a fresh one on navigation, so a
  // failure on one sub-tab never sticks when returning to a healthy sibling.
  return (
    <FeatureErrorBoundary key={search.activeSubtab} fallbackTitle="No se pudo cargar esta sección.">
      {content}
    </FeatureErrorBoundary>
  );
}
